import type { DatabookSheetPreview } from "@/lib/api/databook";

// These are the shared Mapping headers, including the shorter trial balance layout.
// Locate them by label so both layouts use the same filters without column offsets.
const FILTER_HEADERS = new Set([
  "GL Account Code/Name", "High-level Category", "Mapping Classification",
  "Accounts Normalisation", "Sign",
]);

export type MappingFilterSelection = ReadonlyMap<number, ReadonlySet<string>>;
export interface MappingFilterColumn {
  index: number;
  label: string;
  values: string[];
}
export interface MappingFilterModel {
  headerRow: number;
  columns: MappingFilterColumn[];
  valuesByRow: Map<number, Map<number, string>>;
}

export function mappingFilterModel(sheet: DatabookSheetPreview | null): MappingFilterModel | null {
  if (sheet?.name !== "Mapping") return null;
  const header = sheet.cells.find(cell => cell.display_text === "GL Account Code/Name");
  if (!header) return null;
  const headerCells = sheet.cells.filter(cell => cell.row === header.row);
  const filterIndexes = new Set(headerCells
    .filter(cell => FILTER_HEADERS.has(cell.display_text)).map(cell => cell.column));
  const sign = headerCells.find(cell => cell.display_text === "Sign");
  if (sign) {
    const headersByColumn = new Map(headerCells.map(cell => [cell.column, cell]));
    // The generator inserts Mapping levels and custom dimensions directly after
    // Sign, before the spacer/period block. Their labels and count vary by project.
    for (let index = sign.column + 1; index < sheet.column_count; index += 1) {
      const cell = headersByColumn.get(index);
      if (!cell || cell.kind !== "string" || !cell.display_text.trim()
        || /^(19|20)\d{2}[-/. ]?\d{0,2}$/.test(cell.display_text.trim())) break;
      filterIndexes.add(index);
    }
  }
  const columns = headerCells
    .filter(cell => filterIndexes.has(cell.column))
    .map(cell => ({ index: cell.column, label: cell.display_text, values: [] as string[] }));
  const columnIndexes = new Set(columns.map(column => column.index));
  const valuesByRow = new Map<number, Map<number, string>>();
  for (const cell of sheet.cells) {
    if (cell.row <= header.row || !columnIndexes.has(cell.column)) continue;
    let values = valuesByRow.get(cell.row);
    if (!values) {
      values = new Map();
      valuesByRow.set(cell.row, values);
    }
    values.set(cell.column, cell.display_text);
  }
  for (const column of columns) {
    column.values = [...new Set(sheet.rows.filter(row => row.index > header.row)
      .map(row => valuesByRow.get(row.index)?.get(column.index) ?? ""))]
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }
  return { headerRow: header.row, columns, valuesByRow };
}

export function availableMappingFilterValues(
  sheet: DatabookSheetPreview,
  model: MappingFilterModel,
  filters: MappingFilterSelection,
  column: MappingFilterColumn,
): string[] {
  // Ignore this column's own filter so its unchecked options remain available.
  const otherFilters = [...filters].filter(([index]) => index !== column.index);
  if (otherFilters.length === 0) return column.values;
  const available = new Set<string>();
  for (const row of sheet.rows) {
    if (row.index <= model.headerRow) continue;
    const values = model.valuesByRow.get(row.index);
    if (otherFilters.every(([index, selected]) => selected.has(values?.get(index) ?? ""))) {
      available.add(values?.get(column.index) ?? "");
    }
  }
  return column.values.filter(value => available.has(value));
}

export function filterMappingSheet(
  sheet: DatabookSheetPreview,
  model: MappingFilterModel,
  filters: MappingFilterSelection,
): DatabookSheetPreview {
  if (filters.size === 0) return sheet;
  const rows = sheet.rows.filter(row => row.index <= model.headerRow
    || [...filters].every(([column, values]) => values.has(model.valuesByRow.get(row.index)?.get(column) ?? "")));
  const projectedIndexes = new Map(rows.map((row, index) => [row.index, index]));
  return {
    ...sheet,
    row_offset: 0,
    row_count: rows.length,
    returned_row_count: rows.length,
    // Keep source_index for Excel addresses while compacting visible grid positions.
    rows: rows.map((row, index) => ({ ...row, index })),
    cells: sheet.cells.flatMap(cell => {
      const row = projectedIndexes.get(cell.row);
      return row === undefined ? [] : [{ ...cell, row }];
    }),
    merges: sheet.merges.flatMap(merge => {
      const start = projectedIndexes.get(merge.start_row);
      const end = projectedIndexes.get(merge.end_row);
      return start === undefined || end === undefined ? [] : [{ ...merge, start_row: start, end_row: end }];
    }),
  };
}
